// §I silhouette — ported VERBATIM from the R10 §I standalone `chartSVG()` / `G` /
// `yVal` / `smoothPath()` (Claude Design canon, file F4). Every element CD draws is
// here: the ±2 / 0 hairlines with their value labels, the area fill, the per-stage
// projection lines, the radial halo + glow on the current node, the CD smoothed
// stroke, the baseline with end ticks, the differentiated stage dots (ember current,
// amber peak, paper rest), the density-aware x-axis year ticks, and the PEAK marker.
//
// Notes on canon behaviour (all from F4):
// - The line IS smoothed, via §I's own serialiser (`smoothPathCover`, space-joined).
//   The "unsmoothed" eyebrow caption is design copy, not a geometry instruction.
// - YOU ARE HERE has no sub-label in F4 — the ember node + halo carry that meaning
//   (R9 decision). Only PEAK is drawn as a sub-marker.
// - x-axis: `tickLabels[i] === null` → the tick geometry stays, only its label is
//   omitted (s318.d1). Density (N ≥ 10): font 10→8.5px, tracking .16em→.10em.
// - `currentIdx === -1` (no "now" stage) → halo, glow and ember node all drop; the
//   last tick then shows a year instead of NOW.
// - Labels default to the §III-canonical stage dates (mapping spec: §I year axis
//   syncs to §III); pass an explicit `tickLabels` to override.

import { Fragment } from 'react';
import { smoothPathCover } from './curve.js';
import { deriveTickLabels } from './tickLabels.js';

// geometry — verbatim CD `chartSVG` constants.
const W = 1000, H = 300, PAD_X = 24, Y_TOP = 30, Y_BOT = 244;
const Y_MID = (Y_TOP + Y_BOT) / 2;
const TICK_Y = Y_BOT + 26;
const SUB_Y = Y_BOT + 40;
const yVal = (v) => Y_MID - (v / 3) * ((Y_BOT - Y_TOP) / 2);
const SHAPELESS = ['plateau', 'high-held-collapse', 'irregular'];
const f = (n) => n.toFixed(2);

// Подписи оси вынесены в `tickLabels.js` — их считает шов (`viewI`), чарт печатает готовое.
// Проп `stages` оставлен для бордов, которые всё ещё передают этапы напрямую.
export default function SilhouetteChart({ silhouette, currentIdx, geometryClass, stages = null, tickLabels = null, gid = 'i' }) {
  if (!silhouette || silhouette.length === 0) return null;
  const vals = silhouette;
  const N = vals.length;
  const dense = N >= 10;
  // -1 ⇒ no current stage. Clamp out-of-range indices (a stages/silhouette desync can
  // hand us cur ≥ N) to "no current" so the halo / ember / NOW markers simply drop
  // instead of dereferencing pts[cur] and crashing.
  const rawCur = currentIdx == null ? N - 1 : currentIdx;
  const cur = rawCur >= 0 && rawCur < N ? rawCur : -1;
  const xOf = (i) => (N === 1 ? W / 2 : PAD_X + (i / (N - 1)) * (W - 2 * PAD_X));
  const pts = vals.map((val, i) => ({ x: xOf(i), y: yVal(val) }));
  const stroke = smoothPathCover(pts.map((p) => [p.x, p.y])); // §I Catmull-Rom (space-joined)
  const area = `${stroke} L ${f(pts[N - 1].x)} ${Y_BOT} L ${f(pts[0].x)} ${Y_BOT} Z`;
  const max = Math.max(...vals);
  const peakIdx = vals.indexOf(max);
  const shapeless = SHAPELESS.includes(geometryClass);
  // Максимум средней линии делят два этапа и больше — пика нет, PEAK не рисуется (`s988.d3`, тикет
  // `3f819875cbb8817d9165ef46531db9df` п. 3). Сравнение по тем же средним, что и `indexOf(max)`. Ровная
  // линия (проход CD 12.10, ввод «2 этапа»: средняя 0/0, класс КРАСНОЙ линии `monotonic-ascent`) — частный
  // случай этого правила, а не второе правило рядом: до `s988.d3` здесь стоял отдельный гейт ровности, и
  // `indexOf(max)` на 2 / 2 / 0 по-прежнему ставил PEAK на первый из равных.
  const tied = vals.filter((v) => v === max).length > 1;
  const showPeak = !shapeless && !tied && peakIdx >= 0 && peakIdx !== cur; // axis-B ∅ forms + collision rule (CD s345.d1); peakIdx<0 guards a non-finite silhouette (Math.max→NaN→indexOf→−1)
  const labels = tickLabels ?? deriveTickLabels(stages, N, cur);

  return (
    <svg className="cv-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-field="silhouette_chart" role="img" aria-label="silhouette — mean of seven layers per stage">
      <defs>
        <linearGradient id={`fill-${gid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#D48A3C" stopOpacity=".32" />
          <stop offset="100%" stopColor="#C8482B" stopOpacity=".08" />
        </linearGradient>
        <radialGradient id={`halo-${gid}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C8482B" stopOpacity=".55" />
          <stop offset="60%" stopColor="#C8482B" stopOpacity=".10" />
          <stop offset="100%" stopColor="#C8482B" stopOpacity="0" />
        </radialGradient>
        <filter id={`glow-${gid}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" />
        </filter>
      </defs>

      {/* hairlines at +2 / 0 / -2 with their value labels — flat line/text siblings
          (CD emits no wrapping <g>) */}
      {[2, 0, -2].map((val) => {
        const y = yVal(val);
        const z = val === 0;
        return (
          <Fragment key={val}>
            <line x1={PAD_X - 8} y1={y} x2={W - PAD_X + 8} y2={y} stroke={z ? 'rgba(143,136,122,.20)' : 'rgba(143,136,122,.10)'} strokeWidth={z ? '.6' : '.5'} strokeDasharray={z ? '2 6' : '1 4'} />
            <text x={PAD_X - 16} y={y + 3} textAnchor="end" fontFamily="JetBrains Mono, monospace" fontSize="8.5" letterSpacing=".12em" fill={z ? '#5A554A' : 'rgba(90,85,74,.55)'}>
              {val > 0 ? `+${val}` : val}
            </text>
          </Fragment>
        );
      })}

      {/* area fill */}
      <path d={area} fill={`url(#fill-${gid})`} opacity=".55" />

      {/* per-stage projection lines down to the baseline */}
      {pts.map((p, i) => (
        <line key={`proj-${i}`} x1={f(p.x)} y1={f(p.y + 3.5)} x2={f(p.x)} y2={Y_BOT} stroke="rgba(143,136,122,.30)" strokeWidth=".5" strokeDasharray="1 2.5" />
      ))}

      {/* halo + glow on the current node (dropped when there is no current stage) —
          flat circles, no wrapping <g> (CD) */}
      {cur >= 0 && (
        <>
          <circle cx={f(pts[cur].x)} cy={f(pts[cur].y)} r="34" fill={`url(#halo-${gid})`} />
          <circle cx={f(pts[cur].x)} cy={f(pts[cur].y)} r="9" fill="#C8482B" opacity=".5" filter={`url(#glow-${gid})`} />
        </>
      )}

      {/* the smoothed silhouette line */}
      <path d={stroke} fill="none" stroke="#D48A3C" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />

      {/* baseline + end ticks */}
      <line x1={PAD_X} y1={Y_BOT} x2={W - PAD_X} y2={Y_BOT} stroke="rgba(143,136,122,.28)" strokeWidth=".6" />
      <line x1={PAD_X} y1={Y_BOT - 4} x2={PAD_X} y2={Y_BOT + 4} stroke="rgba(143,136,122,.4)" strokeWidth=".6" />
      <line x1={W - PAD_X} y1={Y_BOT - 4} x2={W - PAD_X} y2={Y_BOT + 4} stroke="rgba(143,136,122,.4)" strokeWidth=".6" />

      {/* stage dots — ember current (double circle), amber peak, paper rest */}
      {pts.map((p, i) => {
        if (i === cur) {
          return (
            <g key={`dot-${i}`} data-field="silhouette_now_marker">
              <circle cx={f(p.x)} cy={f(p.y)} r="5.4" fill="#C8482B" />
              <circle cx={f(p.x)} cy={f(p.y)} r="2.4" fill="#0a0b0d" />
            </g>
          );
        }
        const isPeak = showPeak && i === peakIdx;
        return <circle key={`dot-${i}`} cx={f(p.x)} cy={f(p.y)} r={dense ? (isPeak ? 3 : 2.3) : isPeak ? 3.6 : 2.8} fill={isPeak ? '#D48A3C' : '#E8E2D4'} />;
      })}

      {/* x-axis year labels — one per stage at its (even) x. tickLabels[i] === null
          drops the label but not the tick; density shrinks font/tracking at N ≥ 10.
          Placeholder only when no dates exist at all. */}
      <g data-field="silhouette_xaxis" {...(labels ? {} : { 'data-placeholder': 'true' })}>
        {labels &&
          pts.map((p, i) => {
            const lab = labels[i];
            if (lab === null || lab === undefined) return null;
            const isCur = i === cur;
            const isPeak = showPeak && i === peakIdx;
            return (
              <text
                key={`yr-${i}`}
                x={f(p.x)}
                y={TICK_Y}
                textAnchor="middle"
                fontFamily="JetBrains Mono, monospace"
                fontSize={dense ? '8.5' : '10'}
                letterSpacing={dense ? '.10em' : '.16em'}
                fontWeight={isCur ? 500 : 400}
                fill={isCur ? '#C8482B' : isPeak ? '#D48A3C' : '#5A554A'}
              >
                {lab}
              </text>
            );
          })}
      </g>

      {/* PEAK sub-marker */}
      {showPeak && (
        <text data-field="silhouette_peak_marker" x={f(pts[peakIdx].x)} y={SUB_Y} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="7.5" letterSpacing=".26em" fill="#5A554A">PEAK</text>
      )}
    </svg>
  );
}
